import { FC, useMemo, useRef, useState } from 'react'
import { Button, FetchState, LoadingIndicator, useModal } from '../../../../../../components'
import { ButtonType, InputType, showAlert, showError } from '../../../../../../../_metronic/helpers'
import { AnexosInterconsultaModal } from '../../../../pareceres/pareceres-list/components/modal/AnexosInterconsultaModal'
import { InfoAlert } from '../../../../../../components/Info/InfoAlert'
import {
  AtendimentoDto,
  AtendimentoDtoRespostaZerada,
  MOTIVOS_RESPOSTA_ZERADA_CONHECIDOS,
} from '../../../../atendimento/core/_models'
import {
  FormularioAtendimentoView,
  FormularioRespostaList,
} from '../../../../avaliacoes/avaliacoes-list/core/_models'
import { enviarFormulario } from '../../../../avaliacoes/avaliacoes-list/core/_requests'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  CampoResolvido,
  FormularioEnvioDto,
  FormularioResolvido,
  RespostaEnvio,
} from '../../../../formularios/formularios-create/core/_models'
import {
  FormularioPreenchimento,
  ValoresPreenchimento,
} from '../../../../formularios/preenchimento/FormularioPreenchimento'
import { ValorCampo } from '../../../../formularios/preenchimento/CampoPreenchimento'
import { normalizar } from '../../../../formularios/core/condicao/valores'
import {
  CODIGO_RESPOSTAS_INVALIDAS,
  extrairErrosFormulario,
} from '../../../../formularios/core/errosFormulario'

type IProps = {
  atendimentoId: number
  onSubmit?: () => void
  isInterconsulta?: boolean
  /**
   * Rota do envio. Padrão: a genérica de formulário de atendimento. A solicitação da interconsulta
   * usa a rota própria (`enviarFormularioSolicitacao`), única aberta ao perfil solicitante.
   */
  enviar?: (
    atendimentoId: number,
    idFormularioAtendimento: number,
    envio: FormularioEnvioDto
  ) => Promise<AtendimentoDto>
  /**
   * Próximo passo depois de salvar, no lugar do aviso de sucesso: o modal fecha e o chamador segue
   * (ex.: o parecer da interconsulta vai direto para finalizar e assinar). Não é chamado quando o
   * envio zerou respostas — aí o modal fica aberto para o profissional reconferir.
   */
  aoConcluir?: () => void
}

type ValoresIniciais = {
  /** O que a tela **exibe** ao abrir: resposta gravada ou, na falta dela, a resposta padrão. */
  valores: ValoresPreenchimento
  /**
   * O que **veio gravado do servidor**, sem resposta padrão nenhuma.
   *
   * É a base de comparação do envio, e por isso não pode ser o mesmo mapa de `valores`: resposta
   * padrão é sugestão de preenchimento, não registro. Se ela entrasse aqui, um campo que o
   * profissional vê marcado contaria como "não alterado", nunca seria gravado e — sendo
   * obrigatório — o servidor o acusaria de faltante, deixando o formulário insubmissível.
   */
  gravados: ValoresPreenchimento
  /** Textos de apoio: resposta anterior que não casou com nenhuma opção atual do campo. */
  ajudas: Record<string, string>
}

const vazio = (campo: CampoResolvido): ValorCampo =>
  campo.tipo === InputType.Select && campo.multiplo ? [] : ''

/**
 * Respostas que o backend apagou nesta operação, no item do formulário que acabou de ser enviado.
 *
 * Nada é inferido no cliente: se o campo vier ausente, vazio ou só com motivo que esta versão da
 * tela não conhece, não há aviso nenhum.
 */
export const respostasZeradasDoEnvio = (
  atendimento: AtendimentoDto | undefined,
  idFormularioAtendimento: number
): AtendimentoDtoRespostaZerada[] => {
  const formulario = atendimento?.formularios?.find((item) => item.id === idFormularioAtendimento)
  return (formulario?.respostasZeradas ?? []).filter((zerada) =>
    (MOTIVOS_RESPOSTA_ZERADA_CONHECIDOS as readonly string[]).includes(zerada.motivo)
  )
}

const ehCampoDeOpcao = (campo: CampoResolvido): boolean =>
  campo.tipo === InputType.Radio || campo.tipo === InputType.Select

/** Casa um texto (resposta legada ou resposta padrão) com o código da opção atual. */
const codigoPelaDescricao = (campo: CampoResolvido, texto: string): string | undefined =>
  campo.opcoes.find((opcao) => normalizar(opcao.descricao) === normalizar(texto))?.codigo

const respostaDoCampo = (
  campo: CampoResolvido,
  respostas: FormularioRespostaList[]
): FormularioRespostaList | undefined =>
  respostas.find((resposta) => resposta.codigoCampo && resposta.codigoCampo === campo.codigo) ??
  respostas.find((resposta) => resposta.campo === campo.rotulo && resposta.ordem === campo.ordem)

/**
 * O valor que veio **gravado** para este campo, na forma que o renderizador usa.
 *
 * Espelha a leitura de `montarValoresIniciais` **menos a resposta padrão**: campo nunca respondido
 * fica vazio aqui, mesmo que a tela vá exibi-lo preenchido com o padrão. Resposta gravada que o
 * esquema atual não interpreta também fica vazia — é o que mantém a proteção da rodada 19, já que
 * ela não é exibida e, portanto, não pode contar como alteração.
 */
const valorGravado = (
  campo: CampoResolvido,
  resposta: FormularioRespostaList | undefined
): ValorCampo => {
  if (!resposta) {
    return vazio(campo)
  }
  if (!ehCampoDeOpcao(campo)) {
    return resposta.valor ?? ''
  }
  const codigos = resposta.codigosOpcao?.filter((codigo) => !!codigo) ?? []
  if (codigos.length > 0) {
    return campo.multiplo ? codigos : codigos[0]
  }
  const textos =
    resposta.valores && resposta.valores.length > 0
      ? resposta.valores
      : resposta.valor
      ? [resposta.valor]
      : []
  const casados = textos
    .map((texto) => codigoPelaDescricao(campo, texto))
    .filter((codigo): codigo is string => !!codigo)
  if (casados.length === 0) {
    return vazio(campo)
  }
  return campo.multiplo ? casados : casados[0]
}

/**
 * Valores iniciais do preenchimento.
 *
 * Em campo de opção o valor é o **código**: vem de `codigosOpcao` quando o backend já grava, senão
 * é casado pela descrição atual (normalizada). Sem casamento, o campo abre vazio e mostra a
 * resposta anterior como texto de apoio — nunca reenvia um texto que o formulário não aceita mais.
 */
export const montarValoresIniciais = (
  campos: CampoResolvido[],
  respostas: FormularioRespostaList[],
  t: (id: string, valores?: Record<string, unknown>) => string
): ValoresIniciais => {
  const valores: ValoresPreenchimento = {}
  const gravados: ValoresPreenchimento = {}
  const ajudas: Record<string, string> = {}

  campos.forEach((campo) => {
    const resposta = respostaDoCampo(campo, respostas)
    gravados[campo.codigo] = valorGravado(campo, resposta)

    if (!ehCampoDeOpcao(campo)) {
      valores[campo.codigo] = resposta?.valor ?? campo.respostaPadrao ?? ''
      return
    }

    const codigos = resposta?.codigosOpcao?.filter((codigo) => !!codigo) ?? []
    if (codigos.length > 0) {
      valores[campo.codigo] = campo.multiplo ? codigos : codigos[0]
      return
    }

    const textos = resposta
      ? resposta.valores && resposta.valores.length > 0
        ? resposta.valores
        : resposta.valor
        ? [resposta.valor]
        : []
      : campo.respostaPadrao
      ? [campo.respostaPadrao]
      : []

    const casados = textos
      .map((texto) => codigoPelaDescricao(campo, texto))
      .filter((codigo): codigo is string => !!codigo)

    if (casados.length === 0) {
      valores[campo.codigo] = vazio(campo)
      if (textos.length > 0 && resposta) {
        ajudas[campo.codigo] = t('formularios.preenchimento.respostaAnterior', {
          valor: textos.join(', '),
        })
      }
      return
    }
    valores[campo.codigo] = campo.multiplo ? casados : casados[0]
  })

  return { valores, gravados, ajudas }
}

export const FormularioModal: FC<IProps> = ({
  atendimentoId,
  onSubmit,
  isInterconsulta,
  enviar = enviarFormulario,
  aoConcluir,
}) => {
  const { fetchStates, closeModal, getPersistedData, persistData, showModal } = useModal()
  const { t } = useT()

  const { data: formularioAtendimento } = (fetchStates['formularioAtendimento'] ||
    {}) as FetchState<FormularioAtendimentoView>
  const { data: formularioResolvido } = (fetchStates['formularioResolvido'] ||
    {}) as FetchState<FormularioResolvido>

  const [enviando, setEnviando] = useState(false)
  const [errosExternos, setErrosExternos] = useState<Record<string, string>>({})
  const [respostasZeradas, setRespostasZeradas] = useState<AtendimentoDtoRespostaZerada[]>([])
  const [nadaASalvar, setNadaASalvar] = useState(false)
  const valoresAtuais = useRef<ValoresPreenchimento>({})

  const campos = useMemo(() => formularioResolvido?.campos ?? [], [formularioResolvido])
  const idFormularioAtendimento =
    formularioResolvido?.idFormularioAtendimento ?? Number(formularioAtendimento?.id)
  const chavePersistencia = `formulario-${atendimentoId}-${idFormularioAtendimento}`

  const { valores, gravados, ajudas } = useMemo(
    () => montarValoresIniciais(campos, formularioAtendimento?.respostas ?? [], t),
    [campos, formularioAtendimento, t]
  )

  const persistidos = getPersistedData(chavePersistencia) as ValoresPreenchimento | null

  const handleCancel = () => {
    persistData(chavePersistencia, valoresAtuais.current)
    closeModal()
  }

  const handleSubmit = async (respostas: RespostaEnvio[]) => {
    setNadaASalvar(false)
    if (respostas.length === 0 && formularioAtendimento?.respondido) {
      /*
       * Nada mudou e o formulário já está respondido: não há resposta a gravar nem transição a
       * provocar. Nem chama o backend — avisa na própria tela, com o drawer aberto.
       *
       * A condição exige `respondido` de propósito: o /enviar também marca o formulário como
       * respondido e dispara o evento que cria a triagem, então um no-op cego engoliria essa
       * transição no primeiro envio. Nesse caso a requisição sai, com a lista vazia.
       */
      setRespostasZeradas([])
      // Com próximo passo, "nada a salvar" não é beco sem saída: o que está gravado já vale e o
      // fluxo segue (ex.: parecer salvo antes, finalizado agora).
      if (aoConcluir) {
        closeModal()
        aoConcluir()
        return
      }
      setNadaASalvar(true)
      return
    }
    setEnviando(true)
    setErrosExternos({})
    setRespostasZeradas([])
    try {
      const atendimento = await enviar(atendimentoId, idFormularioAtendimento, { respostas })
      onSubmit?.()
      persistData(chavePersistencia, null)

      const zeradas = respostasZeradasDoEnvio(atendimento, idFormularioAtendimento)
      if (zeradas.length > 0) {
        /* O modal fica aberto para o aviso ter onde morar: o profissional reconfere e conclui de
           novo. O salvamento já aconteceu — o aviso não é erro e não impede concluir. */
        setRespostasZeradas(zeradas)
        return
      }

      if (isInterconsulta) {
        // Solicitação enviada: segue para anexar os exames, pelas rotas próprias da interconsulta
        // (abertas ao perfil solicitante; a interconsulta acabou de ser criada, então aguarda).
        showModal({
          title: 'Anexar exames',
          content: <AnexosInterconsultaModal atendimentoId={atendimentoId} editavel />,
        })
        return
      }
      if (aoConcluir) {
        closeModal()
        aoConcluir()
        return
      }
      await showAlert({
        title: 'Sucesso!',
        text: `Respostas ${
          formularioAtendimento?.respostas && formularioAtendimento.respostas.length > 0
            ? 'alteradas'
            : 'cadastradas'
        } com sucesso`,
        icon: 'success',
      })
      closeModal()
    } catch (error) {
      const estruturado = extrairErrosFormulario(error)
      if (estruturado?.code === CODIGO_RESPOSTAS_INVALIDAS) {
        const mapa: Record<string, string> = {}
        estruturado.codigosFaltantes.forEach((codigo) => {
          mapa[codigo] = t('formularios.preenchimento.obrigatorio')
        })
        estruturado.codigosInvalidos.forEach((codigo) => {
          mapa[codigo] = estruturado.message
        })
        setErrosExternos(mapa)
        return
      }
      showError(error)
    } finally {
      setEnviando(false)
    }
  }

  return (
    <FormularioPreenchimento
      key={campos.length}
      campos={campos}
      aviso={
        /* Um único ponto de montagem e estados mutuamente exclusivos: os dois avisos nunca
           coexistem, então não disputam espaço nem anunciam em duplicidade no leitor de tela. */
        respostasZeradas.length > 0 || nadaASalvar ? (
          <div role='status' aria-live='polite'>
            {respostasZeradas.length > 0 ? (
              <InfoAlert variant='warning' title={t('formularios.preenchimento.zeradas.titulo')}>
                {t('formularios.preenchimento.zeradas.corpo', {
                  rotulos: respostasZeradas.map((zerada) => zerada.rotulo).join(', '),
                })}
              </InfoAlert>
            ) : (
              <InfoAlert variant='info'>{t('formularios.preenchimento.nadaASalvar')}</InfoAlert>
            )}
          </div>
        ) : undefined
      }
      variaveis={formularioResolvido?.variaveis}
      valoresIniciais={persistidos ?? valores}
      valoresGravados={gravados}
      ajudas={ajudas}
      errosExternos={errosExternos}
      enviando={enviando}
      onValoresChange={(atuais) => {
        valoresAtuais.current = atuais
        /* Editar qualquer campo torna o "Nada a salvar" falso na hora. O aviso de respostas
           apagadas **não** é limpo aqui de propósito: ele pede para reconferir o formulário, então
           precisa continuar à vista enquanto o usuário faz exatamente isso. */
        setNadaASalvar((atual) => (atual ? false : atual))
      }}
      onSubmit={handleSubmit}
      renderAcoes={() => (
        <div className='d-flex justify-content-center'>
          <Button
            type={ButtonType.Reset}
            onClick={handleCancel}
            disabled={enviando}
            className='btn-secondary me-2'
          >
            {t('common.cancel')}
          </Button>
          <Button type={ButtonType.Submit} disabled={enviando} className='btn-primary'>
            {enviando ? <LoadingIndicator /> : t('common.save')}
          </Button>
        </div>
      )}
    />
  )
}
